/**
 * Administrative maintenance page for the network locations displayed by the
 * Outlook add-in. Numeric sort values remain an implementation detail; users
 * manage the order exclusively with DevExtreme row dragging.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Column,
	DataGrid,
	Editing,
	RowDragging,
	Sorting,
} from "devextreme-react/data-grid";
import { LoadPanel } from "devextreme-react/load-panel";
import { Snackbar } from "../../components";
import adminApi from "../../api/admin";
import "./network-location-settings.scss";

const CASE_NUMBER_PATTERN =
	/^(?:\d{2}[A-Z]{3}\d{4}|\d{4}[A-Z]{3}\d{4,6})$/;
export const CASE_NUMBER_FORMAT_MESSAGE =
	"Use 2 digits + 3 letters + 4 digits (12ABC1234), or 4 digits + 3 letters + 4–6 digits (2024ABC1234).";
const UNC_PATH_PATTERN = /^\\\\[^\\/]+\\[^\\/]+(?:\\.*)?$/;

let nextClientKey = 1;

/**
 * Creates a stable browser-only key, including for multiple rows whose API ID is zero.
 *
 * @param {object} location API location.
 * @returns {string} Stable client key.
 */
const createClientKey = (location) =>
	location.clientKey ||
	(location.id > 0
		? `persisted-${location.id}`
		: `new-${nextClientKey++}`);

/**
 * Recalculates hidden sort values so the payload matches the displayed order.
 *
 * @param {Array} locations Ordered rows.
 * @returns {Array} Copied rows with contiguous sort values.
 */
export const resequenceNetworkLocations = (locations) =>
	locations.map((location, index) => ({
		...location,
		sortOrder: (index + 1) * 10,
	}));

/**
 * Converts API rows into stable client rows in persisted order.
 *
 * @param {Array} locations API rows.
 * @returns {Array} Ordered client rows.
 */
export const prepareNetworkLocationRows = (locations = []) => {
	const ordered = [...locations].sort(
		(left, right) =>
			(left.sortOrder ?? 0) - (right.sortOrder ?? 0) ||
			(left.name || "").localeCompare(right.name || "")
	);

	return resequenceNetworkLocations(
		ordered.map((location) => ({
			...location,
			caseNumber: location.caseNumber || "",
			clientKey: createClientKey(location),
		}))
	);
};

/**
 * Moves one row and recalculates the hidden sort values.
 *
 * @param {Array} locations Current ordered rows.
 * @param {number} fromIndex Original row index.
 * @param {number} toIndex Destination row index.
 * @returns {Array} Reordered rows.
 */
export const reorderNetworkLocations = (
	locations,
	fromIndex,
	toIndex
) => {
	if (
		fromIndex === toIndex ||
		fromIndex < 0 ||
		toIndex < 0 ||
		fromIndex >= locations.length ||
		toIndex >= locations.length
	) {
		return locations;
	}

	const reordered = [...locations];
	const [movedLocation] = reordered.splice(fromIndex, 1);
	reordered.splice(toIndex, 0, movedLocation);
	return resequenceNetworkLocations(reordered);
};

/**
 * Removes browser-only state before sending the shared DTO contract.
 *
 * @param {Array} locations Client rows.
 * @returns {Array} API payload rows.
 */
export const createNetworkLocationPayload = (locations) =>
	resequenceNetworkLocations(locations).map(
		({ clientKey, ...location }) => location
	);

/**
 * Validates the complete staged list before starting directory probes on the server.
 *
 * @param {Array} locations Staged rows.
 * @returns {string} An error message, or an empty string when valid.
 */
export const validateNetworkLocations = (locations) => {
	const names = new Set();
	const paths = new Set();
	const activeCaseNumbers = new Set();

	for (const location of locations) {
		const name = (location.name || "").trim();
		const path = (location.location || "").trim();
		const caseNumber = (location.caseNumber || "").trim().toUpperCase();

		if (!name) return "Every network location requires a name.";
		if (name.length > 100) return `${name.slice(0, 100)} exceeds the name limit.`;
		if (!path) return `${name} requires a directory.`;
		if (path.length > 2048) return `${name} exceeds the directory length limit.`;
		if (!UNC_PATH_PATTERN.test(path)) {
			return `${name} must use a UNC path with a server and share.`;
		}
		if (caseNumber.length > 20) {
			return `${name} exceeds the Case Number length limit.`;
		}
		if (caseNumber && !CASE_NUMBER_PATTERN.test(caseNumber)) {
			return `${name} has an invalid Case Number. ${CASE_NUMBER_FORMAT_MESSAGE}`;
		}

		const normalizedName = name.toUpperCase();
		const normalizedPath = path.replace(/\\+$/, "").toUpperCase();
		if (names.has(normalizedName)) {
			return "Network location names must be unique.";
		}
		if (paths.has(normalizedPath)) {
			return "Network location directories must be unique.";
		}
		names.add(normalizedName);
		paths.add(normalizedPath);

		if (location.isActive && caseNumber) {
			if (activeCaseNumbers.has(caseNumber)) {
				return "An active Case Number can be assigned to only one network location.";
			}
			activeCaseNumbers.add(caseNumber);
		}
	}

	return "";
};

/**
 * Renders and manages the administrator-only Network Locations page.
 *
 * @returns {JSX.Element} The maintenance page.
 */
const NetworkLocationSettings = () => {
	const persistedNetworkLocationsRef = useRef([]);
	const saveInProgressRef = useRef(false);
	const [networkLocations, setNetworkLocations] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [snackbarOptions, setSnackbarOptions] = useState({
		visible: false,
		type: "info",
		message: "",
	});

	const showNotification = useCallback((message, type) => {
		setSnackbarOptions({ visible: true, type, message });
	}, []);

	useEffect(() => {
		let isMounted = true;

		/**
		 * Loads the full non-deleted list for administrative maintenance.
		 *
		 * @returns {Promise<void>} Completion of the initial load.
		 */
		const loadNetworkLocations = async () => {
			setIsLoading(true);
			try {
				const locations =
					await adminApi.networkLocations.getNetworkLocationsAsync({
						includeInactive: true,
					});
				if (isMounted) {
					const preparedLocations =
						prepareNetworkLocationRows(locations || []);
					persistedNetworkLocationsRef.current =
						preparedLocations.map((location) => ({ ...location }));
					setNetworkLocations(
						preparedLocations.map((location) => ({ ...location }))
					);
				}
			} catch (error) {
				if (isMounted) {
					showNotification(
						error.message || "Failed to load network locations.",
						"error"
					);
				}
			} finally {
				if (isMounted) setIsLoading(false);
			}
		};

		loadNetworkLocations();
		return () => {
			isMounted = false;
		};
	}, [showNotification]);

	/**
	 * Persists a complete candidate list and restores the last confirmed state
	 * if client validation, directory validation, or persistence fails.
	 *
	 * @param {Array} candidateLocations Complete candidate list.
	 * @returns {Promise<boolean>} Whether the server accepted the update.
	 */
	const persistNetworkLocations = useCallback(
		async (candidateLocations) => {
			if (saveInProgressRef.current) return false;

			const normalizedLocations = resequenceNetworkLocations(
				candidateLocations.map((location) => ({ ...location }))
			);
			const validationMessage =
				validateNetworkLocations(normalizedLocations);
			if (validationMessage) {
				setNetworkLocations(
					persistedNetworkLocationsRef.current.map((location) => ({
						...location,
					}))
				);
				showNotification(validationMessage, "error");
				return false;
			}

			saveInProgressRef.current = true;
			setIsSubmitting(true);
			try {
				const savedLocations =
					await adminApi.networkLocations.updateNetworkLocationsAsync(
						createNetworkLocationPayload(normalizedLocations)
					);
				const preparedLocations =
					prepareNetworkLocationRows(savedLocations || []);
				persistedNetworkLocationsRef.current =
					preparedLocations.map((location) => ({ ...location }));
				setNetworkLocations(
					preparedLocations.map((location) => ({ ...location }))
				);
				showNotification(
					"Network locations were successfully updated.",
					"success"
				);
				return true;
			} catch (error) {
				setNetworkLocations(
					persistedNetworkLocationsRef.current.map((location) => ({
						...location,
					}))
				);
				showNotification(
					error.message || "Failed to update network locations.",
					"error"
				);
				return false;
			} finally {
				saveInProgressRef.current = false;
				setIsSubmitting(false);
			}
		},
		[showNotification]
	);

	/**
	 * Persists the list immediately after a drag-and-drop reorder.
	 *
	 * @param {object} event DevExtreme reorder event.
	 */
	const handleReorder = useCallback(
		(event) => {
			const reorderedLocations = reorderNetworkLocations(
				networkLocations,
				event.fromIndex,
				event.toIndex
			);
			if (reorderedLocations === networkLocations) return;

			setNetworkLocations(reorderedLocations);
			event.promise = persistNetworkLocations(reorderedLocations);
		},
		[networkLocations, persistNetworkLocations]
	);

	/**
	 * Persists DevExtreme's data source when the inline row Save button,
	 * row insertion, or confirmed row deletion completes.
	 *
	 * @param {object} event DevExtreme saved event.
	 */
	const handleGridSaved = useCallback(
		(event) => {
			const dataSource =
				event.component.option("dataSource") || networkLocations;
			const candidateLocations = resequenceNetworkLocations(
				Array.from(dataSource).map((location) => ({ ...location }))
			);
			setNetworkLocations(candidateLocations);
			return persistNetworkLocations(candidateLocations);
		},
		[networkLocations, persistNetworkLocations]
	);

	/**
	 * Initializes a new row at the end of the configured order.
	 *
	 * @param {object} event DevExtreme initialization event.
	 */
	const initializeNewRow = useCallback(
		(event) => {
			event.data.id = 0;
			event.data.clientKey = createClientKey(event.data);
			event.data.caseNumber = "";
			event.data.sortOrder = (networkLocations.length + 1) * 10;
			event.data.isActive = true;
		},
		[networkLocations.length]
	);

	/**
	 * Uppercases Case Number input while preserving an empty value.
	 *
	 * @param {object} newData Pending row values.
	 * @param {*} value Edited value.
	 */
	const setCaseNumber = useCallback((newData, value) => {
		newData.caseNumber = value ? String(value).trim().toUpperCase() : "";
	}, []);

	const nameRules = useMemo(
		() => [
			{ type: "required", message: "Name is required." },
			{ type: "stringLength", max: 100, message: "Name is too long." },
		],
		[]
	);
	const locationRules = useMemo(
		() => [
			{ type: "required", message: "Directory is required." },
			{
				type: "stringLength",
				max: 2048,
				message: "Directory is too long.",
			},
			{
				type: "pattern",
				pattern: UNC_PATH_PATTERN,
				message: "Enter a UNC path with a server and share.",
			},
		],
		[]
	);
	const caseNumberRules = useMemo(
		() => [
			{
				type: "stringLength",
				max: 20,
				message: "Case Number is too long.",
			},
			{
				type: "pattern",
				pattern: CASE_NUMBER_PATTERN,
				ignoreEmptyValue: true,
				message: CASE_NUMBER_FORMAT_MESSAGE,
			},
		],
		[]
	);

	return (
		<div className="network-location-settings">
			<h2 className="content-block">Network Locations</h2>

			<div className="dx-card wide-card network-location-card">
				<DataGrid
					dataSource={networkLocations}
					keyExpr="clientKey"
					disabled={isSubmitting}
					showBorders={true}
					wordWrapEnabled={true}
					repaintChangesOnly={true}
					onInitNewRow={initializeNewRow}
					onSaved={handleGridSaved}
				>
					<Sorting mode="none" />
					<RowDragging
						allowReordering={!isSubmitting}
						showDragIcons={true}
						onReorder={handleReorder}
					/>
					<Editing
						mode="row"
						allowAdding={!isSubmitting}
						allowUpdating={!isSubmitting}
						allowDeleting={!isSubmitting}
						confirmDelete={true}
					/>

					<Column
						dataField="name"
						caption="Name"
						validationRules={nameRules}
						allowSorting={false}
					/>
					<Column
						dataField="location"
						caption="Location"
						minWidth={320}
						validationRules={locationRules}
						allowSorting={false}
					/>
					<Column
						dataField="caseNumber"
						caption="Case Number"
						setCellValue={setCaseNumber}
						validationRules={caseNumberRules}
						allowSorting={false}
					/>
					<Column
						dataField="isActive"
						caption="Active"
						dataType="boolean"
						allowSorting={false}
					/>
				</DataGrid>
			</div>

			<LoadPanel
				shadingColor="rgba(0,0,0,0.4)"
				visible={isLoading || isSubmitting}
				showIndicator={true}
				shading={true}
				showPane={true}
				position={{ of: ".network-location-card" }}
			/>

			<Snackbar
				message={snackbarOptions.message}
				onHiding={() =>
					setSnackbarOptions((current) => ({
						...current,
						visible: false,
					}))
				}
				of={".content"}
				options={snackbarOptions}
			/>
		</div>
	);
};

export default NetworkLocationSettings;
